/** @jsx isolateComponent */

import { Button } from '../../navigation/Button';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import { buttonNavTheme } from './theme';

import type { ButtonNavItemType, ButtonNavOnChooseItem } from './types';

export type Props<T> = {
  /**
   * The ARIA label. All `<nav>` elements on the page, including this one, should have a globally unique label.
   */
  ariaLabel: string;
  /** Set the buttons to be full width */
  isButtonFullWidth?: boolean;
  /**
   * An array of items to render within the navigation.
   */
  items: Array<T>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: ButtonNavOnChooseItem<T>;
  /**
   * Number of buttons per row. Leave empty to fit all `items`
   * into a single row.
   */
  perRow?: number;
  /**
   * The `key` of the currently selected navigation item. `key` is used because
   * the `Button` component does not require a `href`.  It is also used rather
   * than the full item since the item object could be cloned, which ruins
   * `===` checks.
   */
  selectedItemKey: string | null | undefined;
};

const Styles = labelStyles('ButtonNav', {
  list: (props: Props<any>) => [
    {
      justifyContent: 'center',
      display: 'grid',
      gridTemplateColumns: `repeat(${props.items.length}, minmax(auto, min-content))`,
      gap: 10,
    },
    props.perRow &&
      withModifier(`${props.perRow}-perRow`, {
        gridTemplateColumns: `repeat(${Math.min(
          props.items.length,
          props.perRow
        )}, 1fr)`,
      }),
  ],
  item: {
    display: 'block',
  },
});

/**
 * Creates a horizontal navigation component that can display a variable
 * number of buttons.
 */
export const ButtonNav = <T extends ButtonNavItemType>(props: Props<T>) => {
  const { ariaLabel, items, onChooseItem, selectedItemKey } = props;

  const buttonItems = items.map((item: ButtonNavItemType) => {
    const isSelected = item.key === selectedItemKey;

    return (
      <li css={Styles.item} key={item.key}>
        <Button
          ariaLabel={isSelected ? `${item.text}, selected` : item.text}
          color="white"
          colorTheme={buttonNavTheme}
          hasShadow={isSelected}
          href={item.href}
          iconBefore={item.iconBefore}
          isFullWidth={props.isButtonFullWidth}
          isSelected={isSelected}
          onClick={(event: React.MouseEvent) => onChooseItem(item as T, event)}
        >
          {item.text}
        </Button>
      </li>
    );
  });

  return (
    <nav aria-label={ariaLabel}>
      <ul css={Styles.list(props)}>{buttonItems}</ul>
    </nav>
  );
};

ButtonNav.defaultProps = {
  onChooseItem: () => {},
};
